import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import MainPart from "./MainPart";
import GroupsFeeds from "./GroupsFeeds";
import MobilesTabs from "./MobilesTabs";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import {
  groupDetail,
  groupsListing,
  LeaveGroup,
} from "src/DAL/Community/Community";
import { s3baseUrl } from "src/config/config";
import CustomConfirmation from "src/components/GeneralComponents/CustomConfirmation";
import { show_proper_words } from "src/utils/constants";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrPage from "src/design-system/components/GrPage";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrButton from "src/design-system/components/GrButton";
import GrIcon from "src/design-system/components/GrIcon";
import GrLoader from "src/design-system/components/GrLoader";
import GrDialog from "src/design-system/components/GrDialog";
import GrAvatar from "src/design-system/components/GrAvatar";
import GrRichText from "src/design-system/components/GrRichText";

// Feed column fills the space, groups sidebar beside it (one gap), sticking just under the top bar.
const SIDEBAR_WIDTH = gr.layout.sideColumn;
const STICKY_TOP = `calc(${gr.layout.topBar} + ${gr.space[6]})`;

// One responsive rule (theme breakpoint lg, the same one the app sidebar uses): from lg the groups
// card sits in a right column, below lg the group picker sits above the feed. Exactly one shows.
const bodySx = {
  display: "flex",
  alignItems: "flex-start",
  gap: gr.space[6],
};
const feedColumnSx = {
  flex: "1 1 auto",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: gr.space[4],
};
const sidebarSx = {
  display: { xs: "none", lg: "flex" },
  flex: `0 0 ${SIDEBAR_WIDTH}`,
  minWidth: 0,
  position: "sticky",
  top: STICKY_TOP,
  alignSelf: "flex-start", // its own height, so it can stick while the feed scrolls
};
const groupsCardSx = {
  maxHeight: `calc(100vh - ${STICKY_TOP} - ${gr.space[6]})`,
};
const pickerSx = { display: { xs: "block", lg: "none" } };

// Group image + description: the Group Detail popup here and the /group-feed/:id page.
export function GroupInfo({ group }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: gr.space[4] }}>
      <GrAvatar
        size="lg"
        src={s3baseUrl + group?.image?.thumbnail_1}
        name={group?.title}
        sx={{ borderRadius: gr.radius.tile }}
      />
      {group?.description ? (
        <GrRichText html={group.description} sx={{ flex: 1, minWidth: 0 }} />
      ) : null}
    </Box>
  );
}

GroupInfo.propTypes = {
  group: PropTypes.object,
};

export default function Feed({ groupFeed = false, group = "" }) {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedGroup, setSelectedGroup, userInfo } = useContentSetting();
  const [showInfoBox, setShowInfoBox] = useState(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openGroup, setOpenGroup] = useState(false);
  const [communityGroup, setCommunityGroup] = useState(null);
  // The group whose detail is open (any group in the list, not only the chosen one).
  const [detailGroupId, setDetailGroupId] = useState("");
  const [isGroupLoad, setIsisGroupLoad] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const navigate = useNavigate();

  const getGroupsListing = async () => {
    const result = await groupsListing();
    if (result.code == 200) {
      const savedGroupId = localStorage.getItem("selectedGroup");
      const communityGroups = result?.community_groups || [];

      let initialGroupId = "";

      if (group) {
        // /group-feed/:id shows the group from the URL, selected the way a click in the groups list
        // does (context + localStorage). GroupsFeedsDetail remounts this Feed when :id changes.
        initialGroupId = group;
        localStorage.setItem("selectedGroup", initialGroupId);
      } else if (savedGroupId) {
        initialGroupId = savedGroupId === "general" ? "" : savedGroupId;
      } else if (communityGroups.length > 0) {
        initialGroupId = communityGroups[0]._id;
        localStorage.setItem("selectedGroup", initialGroupId);
      }

      setSelectedGroup(initialGroupId);
      setData(communityGroups);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  const getGroupDetail = async (id) => {
    setIsisGroupLoad(true);
    const result = await groupDetail(id);
    if (result.code === 200) {
      setCommunityGroup(result.community_group);
      setIsisGroupLoad(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsisGroupLoad(false);
    }
  };

  const handleGroupLeave = async () => {
    setOpenDelete(false);
    setOpenGroup(false);
    const result = await LeaveGroup(communityGroup.community_group_slug);
    if (result.code === 200) {
      // Leaving the group being shown goes back to General Community; leaving another keeps the feed.
      if (communityGroup._id === selectedGroup) {
        localStorage.removeItem("selectedGroup");
        setSelectedGroup("");
      }
      setData((prevData) =>
        prevData.filter(
          (group) =>
            group.community_group_slug !== communityGroup.community_group_slug
        )
      );
      enqueueSnackbar("You have Successfully left the Group", {
        variant: "success",
      });
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  useEffect(() => {
    getGroupsListing();
  }, []);

  useEffect(() => {
    if (openGroup) {
      getGroupDetail(detailGroupId || selectedGroup);
    }
  }, [selectedGroup, openGroup, detailGroupId]);

  const handleMyPosts = () => {
    if (selectedGroup) {
      navigate(`/my-posts`, { state: { _id: selectedGroup } });
    } else {
      navigate(`/my-posts`);
    }
  };

  // Group Detail and Leave Group open from the ⋮ on each group (groups list / phone picker).
  const openGroupDetail = (groupId) => {
    setDetailGroupId(groupId);
    setOpenGroup(true);
  };
  // Leave from the list: that group (its slug is in the list data), with the same confirm as before.
  const leaveGroup = (group) => {
    setCommunityGroup(group);
    setOpenDelete(true);
  };

  let name_group = data.find((id) => id._id === selectedGroup);

  if (isLoading) {
    return <GrLoader sx={{ py: gr.space[12] }} />;
  }

  const body = (
    <Box sx={bodySx}>
      <Box sx={feedColumnSx}>
        <Box sx={pickerSx}>
          <MobilesTabs
            data={data}
            onGroupDetail={openGroupDetail}
            onLeaveGroup={leaveGroup}
            groupDetailLoading={isGroupLoad}
          />
        </Box>
        <MainPart
          showInfoBox={showInfoBox}
          setShowInfoBox={setShowInfoBox}
          userInfo={userInfo}
        />
      </Box>
      <Box component="aside" sx={sidebarSx}>
        <GroupsFeeds
          data={data}
          onGroupDetail={openGroupDetail}
          onLeaveGroup={leaveGroup}
          groupDetailLoading={isGroupLoad}
          sx={groupsCardSx}
        />
      </Box>
    </Box>
  );

  // /group-feed/:id has its own page frame and header (GroupsFeedsDetail).
  if (groupFeed) return body;

  return (
    <GrPage sx={{ pb: gr.space[8] }}>
      <GrPageHeader
        title={
          name_group?.title
            ? show_proper_words(name_group.title)
            : "General Community"
        }
        actions={
          <GrButton variant="outline" onClick={handleMyPosts}>
            My Posts
          </GrButton>
        }
      />
      {body}
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title={"Are you sure you want to Leave this Group?"}
        handleAgree={handleGroupLeave}
      />
      {communityGroup && (
        <GrDialog
          // Opens once the group is loaded, so the previous group never shows meanwhile.
          open={openGroup && !isGroupLoad}
          onClose={() => setOpenGroup(false)}
          showClose
          title={communityGroup.title}
          actions={
            <GrButton
              variant="danger"
              startIcon={<GrIcon icon={icons.logOut} />}
              onClick={() => {
                setOpenDelete(true);
              }}
            >
              Leave Group
            </GrButton>
          }
        >
          <GroupInfo group={communityGroup} />
        </GrDialog>
      )}
    </GrPage>
  );
}

Feed.propTypes = {
  groupFeed: PropTypes.bool,
  group: PropTypes.string,
};
